
// src/components/Notifications/index.js
import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchNotifications } from '../../actions/notifications';
import {
  Typography,
  Box,
  Button,
   Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  FormControlLabel,
} from '@mui/material';
import Notification from './Notification/Notification';

const parentPillSx = (isActive) => ({
  borderRadius: 999,
  textTransform: 'none',
  fontWeight: 600,
  fontSize: 13,
  px: 2.2,
  py: 0.6,

  backgroundColor: isActive ? '#333' : '#f3f4f6',
  color: isActive ? 'white' : '#374151',
  borderColor: isActive ? '#2563eb' : '#d1d5db',

  boxShadow: 'none',
  '&:hover': {
    backgroundColor: '#333',
    color: '#fff',
    boxShadow: 'none',
    border: 'none',
  },
  '&:active': { boxShadow: 'none' },
  '&.Mui-focusVisible': { boxShadow: 'none' },
});


// -------- PARENT + CHILD TABS --------

const PARENT_TABS = [
  { key: 'ALL',    label: 'All' },
  { key: 'UNREAD', label: 'Unread' },
  { key: 'EXPIRY', label: 'Expiry Alerts' },
  { key: 'STOCK',  label: 'Stock Alerts' },
];

const EXPIRY_TABS = [
  { key: 'EXP_6M',  label: '6 Months' },
  { key: 'EXP_3M',  label: '3 Months' },
  { key: 'EXP_1M',  label: '1 Month' },
  { key: 'EXP_1W',  label: '1 Week' },
  { key: 'EXP_1D',  label: '1 Day' },
  { key: 'EXPIRED', label: 'Expired' },
];

const STOCK_TABS = [
  { key: 'LOW_STOCK',       label: 'Low Stock' },
  { key: 'CRITICALLY_LOW',  label: 'Critically Low' },
  { key: 'OUT_OF_STOCK',    label: 'Out of Stock' },
];

const Notifications = () => {
  const dispatch      = useDispatch();
  const user          = useSelector(state => state.user);
  const notifications = useSelector(state => state.notifications?.list || []);

  // -------- parent / child tab state --------
  const [parentTab, setParentTab] = useState('ALL');
  const [childTab, setChildTab]   = useState('ALL');

  const handleParentChange = (_, value) => {
    setParentTab(value);
    if (value === 'EXPIRY' || value === 'STOCK') {
    setChildTab('ALL');    // default child filter = All
  } else {
    setChildTab('ALL');    // or null if you prefer, doesn't affect ALL/UNREAD
  }
  };

  // const handleChildChange = (_, value) => {
  //   setChildTab(value);
  // };

  // -------- fetch data once user is known --------
  useEffect(() => {
    if (user.id) dispatch(fetchNotifications(user.id));
  }, [dispatch, user.id]);

  // -------- memo-filtered list --------
  const filtered = useMemo(() => {
    // Base parent filters
    if (parentTab === 'ALL')    return notifications;
    if (parentTab === 'UNREAD') return notifications.filter(n => !n.read);

    // Helper maps
    const expiryMap = {
      EXP_6M:  'EXPIRING_6M',
      EXP_3M:  'EXPIRING_3M',
      EXP_1M:  'EXPIRING_1M',
      EXP_1W:  'EXPIRING_1W',
      EXP_1D:  'EXPIRING_1D',
      EXPIRED: 'EXPIRED',
    };
    const expiryTypes = Object.values(expiryMap);

    const stockMap = {
      LOW_STOCK:      'LOW_STOCK',
      CRITICALLY_LOW: 'CRITICALLY_LOW',
      OUT_OF_STOCK:   'OUT_OF_STOCK',
    };
    const stockTypes = Object.values(stockMap);

    // Expiry parent
if (parentTab === 'EXPIRY') {
  // "ALL" or empty → show all expiry-related
  if (!childTab || childTab === 'ALL') {
    return notifications.filter(n => expiryTypes.includes(n.type));
  }
  return notifications.filter(n => n.type === expiryMap[childTab]);
}

// Stock parent
if (parentTab === 'STOCK') {
  // "ALL" or empty → show all stock-related
  if (!childTab || childTab === 'ALL') {
    return notifications.filter(n => stockTypes.includes(n.type));
  }
  return notifications.filter(n => n.type === stockMap[childTab]);
}

    return notifications;
  }, [parentTab, childTab, notifications]);

  return (
    <Box sx={{ textAlign: 'center', p: 2, backgroundColor: 'white', borderRadius: 1, marginLeft:{xs:'8%', sm:'2%', lg:0} }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '90%',
          margin: 'auto',
          py: '15px',
          pb: '32px',
        }}
      >
        <Typography variant="h3" sx={{ fontSize: {
      xs: '1.6rem',  // mobile
      sm: '2rem',    // small tablets
      md: '2.5rem',  // desktop
    }, fontWeight: 600 }}>
          Alerts
        </Typography>
      </Box>

      {/* Parent Tabs */}
      {/* Parent Tabs – styled like Inventory pills */}
{/* <Box
  sx={{
    width: '90%',
    margin: 'auto',
    mb: 2,
    display: 'flex',
    gap: 1,
    justifyContent: 'flex-start',
  }}
>
  {PARENT_TABS.map((t) => (
    <Button
      key={t.key}
      size="small"
      onClick={() => handleParentChange(null, t.key)}
      variant={parentTab === t.key ? 'contained' : 'outlined'}
      sx={parentPillSx(parentTab === t.key)}
    >
      {t.label}
    </Button>
  ))}
</Box> */}

<Box
  sx={{
    width: '90%',
    margin: 'auto',
    mb: 2,

    // ✅ mobile: 2 rows (2x2). desktop: your old row
    display: { xs: 'grid', sm: 'flex' },
    gridTemplateColumns: { xs: '1fr 1fr', sm: 'none' },
    gap: 1,
    justifyContent: { xs: 'stretch', sm: 'flex-start' },
    alignItems: 'center',
  }}
>
  {PARENT_TABS.map((t) => (
    <Button
      key={t.key}
      size="small"
      onClick={() => handleParentChange(null, t.key)}
      variant={parentTab === t.key ? 'contained' : 'outlined'}
      sx={{
        ...parentPillSx(parentTab === t.key),

        // ✅ mobile-only tweaks
        width: { xs: '100%', sm: 'auto' },
        justifyContent: 'center',
        fontSize: { xs: 12, sm: 13 },
        px: { xs: 1.2, sm: 2.2 },
        py: { xs: 0.9, sm: 0.6 },
        whiteSpace: 'nowrap',
      }}
    >
      {t.label}
    </Button>
  ))}
</Box>


      <Box sx={{ width: '90%', margin: 'auto', backgroundColor: '#f9fafb', pb:2 }}>
      

      {/* Notifications list */}
      
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>


        {(parentTab === 'EXPIRY' || parentTab === 'STOCK') && (
        <Box
  sx={{
    width: { xs: '100%', md: '20%' },
    p: { xs: '10px 12px', md: '0px 20px' },
    border: { xs: '1px solid #ededed', md: '2px solid #ededed' },
    background: 'white',
    pt: { xs: 1, md: 3 },
    mb: { xs: 1.5, md: 0 },
    mt: { xs: 1.5, md: 0 },
    ml: { xs: 0, md: 2 },
    mr: { xs: 0, md: 0 },
    borderRadius: { xs: '12px', md: '10px' },

  }}
>

  {parentTab === 'EXPIRY' && (
    <FormControl sx={{ width: '100%', mb: 3 }}>
      <FormLabel sx={{color:'#333 !important', fontSize:'18px', textAlign:'left', fontWeight:'bold', marginBottom:'10px'}}>Filters</FormLabel>
      <RadioGroup
  row
  value={childTab || ''}
  onChange={(e) => setChildTab(e.target.value)}
  sx={{
  // ✅ mobile stays horizontal scroll
  display: 'flex',
    flexDirection: { xs: 'row', md: 'column' },   // ✅ tablets stay row
    flexWrap: 'nowrap',
    overflowX: { xs: 'auto', md: 'visible' },     // ✅ tablets scroll
    overflowY: { xs: 'hidden', md: 'visible' },
    gap: 1,
    alignItems: { xs: 'center', md: 'flex-start' },
    pb: { xs: 0.5, md: 0 },

    // optional: hides ugly scrollbar on iOS/webkit
    '&::-webkit-scrollbar': { display: 'none' },
    msOverflowStyle: 'none',
    scrollbarWidth: 'none',
}}

>

        <FormControlLabel
          value="ALL"
          control={<Radio />}
          label="All"
          sx={{
  m: 0,
  '& .MuiFormControlLabel-label': {
    fontSize: '13px',
    fontWeight: 500,
    whiteSpace: 'nowrap',
  },
  minWidth: { xs: '70px', sm: 'auto' }
}}

        />
        {EXPIRY_TABS.map((t) => (
          <FormControlLabel
          key={t.key}
    value={t.key}
  control={<Radio />}
  label={t.label}
  sx={{
    minWidth: { xs: 110, sm: 'auto' }, 
    m: 0,
    '& .MuiFormControlLabel-label': {
      fontSize: '13px',
      fontWeight: 500,
      whiteSpace: 'nowrap',
    },
  }}
/>

        ))}
      </RadioGroup>
    </FormControl>
  )}

  {parentTab === 'STOCK' && (
    <FormControl sx={{ width: '100%', mb: 3 }}>
      <FormLabel sx={{color:'#333 !important', fontSize:'18px', textAlign:'left', fontWeight:'bold', marginBottom:'10px'}}>Filters</FormLabel>
      <RadioGroup
  row
  value={childTab || ''}
  onChange={(e) => setChildTab(e.target.value)}
  sx={{
  // ✅ mobile stays horizontal scroll
  display: 'flex',
    flexDirection: { xs: 'row', md: 'column' },   // ✅ tablets stay row
    flexWrap: 'nowrap',
    overflowX: { xs: 'auto', md: 'visible' },     // ✅ tablets scroll
    overflowY: { xs: 'hidden', md: 'visible' },
    gap: 1,
    alignItems: { xs: 'center', md: 'flex-start' },
    pb: { xs: 0.5, md: 0 },

    // optional: hides ugly scrollbar on iOS/webkit
    '&::-webkit-scrollbar': { display: 'none' },
    msOverflowStyle: 'none',
    scrollbarWidth: 'none',
}}

>
        <FormControlLabel
  value="ALL"
  control={<Radio />}
  label="All"
  sx={{
  m: 0,
  '& .MuiFormControlLabel-label': {
    fontSize: '13px',
    fontWeight: 500,
    whiteSpace: 'nowrap',
  },
  minWidth: { xs: '70px', sm: 'auto' },
}}

/>
        {STOCK_TABS.map((t) => (
          <FormControlLabel
  key={t.key}
    value={t.key}
  control={<Radio />}
  label={t.label}
  sx={{
    minWidth: { xs: 110, sm: 'auto' },
    m: 0,
    '& .MuiFormControlLabel-label': {
      fontSize: '13px',
      fontWeight: 500,
      whiteSpace: 'nowrap',
    },
  }}
/>

        ))}
      </RadioGroup>
    </FormControl>
  )}
</Box>
        )}
      <Box sx={{display:'flex', flexDirection:'column', width:'100%', marginTop:'15px'}}>
        {filtered && filtered.length > 0 ? (
          filtered.map(n => (
            <Notification key={n._id || n.id} notification={n} />
          ))
        ) : (
          <Typography variant="body1" sx={{ color: 'text.secondary', mt: 4 }}>
            No alerts to show.
          </Typography>
        )}
        </Box>
      </Box>
    </Box>
    </Box>
  );
};

export default Notifications;

